/** Character-independent AgentOS avatar runtime contract. */

export const AGENTOS_AVATAR_RUNTIME_PROTOCOL = 'agentos.avatar-runtime.v1' as const

export const AGENTOS_AVATAR_ROLES = [
  'idle',
  'listening',
  'thinking',
  'talk_short',
  'talk_medium',
  'talk_long',
  'happy',
  'joyful',
  'sad',
  'greeting',
] as const

export type AgentOSAvatarRole = (typeof AGENTOS_AVATAR_ROLES)[number]
export type AgentOSAvatarPhase =
  | 'greeting'
  | 'idle'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'error'
export type AgentOSAvatarAffect = 'neutral' | 'pleased' | 'concerned'
export type AgentOSAvatarAffectIntensity = 'low' | 'high'
export const AGENTOS_AVATAR_AFFECT_TOOL = 'report_affect' as const
export type AgentOSAvatarVisualState =
  | 'idle'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'pleased'
  | 'concerned'
  | 'error'
  | 'greeting'

export interface AgentOSAvatarProjection {
  protocol: typeof AGENTOS_AVATAR_RUNTIME_PROTOCOL
  projection_revision: number
  session_id: string
  turn_id: string | null
  sequence: number
  occurred_at: string
  phase: AgentOSAvatarPhase
  speaking_role: Extract<AgentOSAvatarRole, `talk_${string}`> | null
  affect: AgentOSAvatarAffect
  affect_intensity: AgentOSAvatarAffectIntensity | null
}

export type AgentOSAvatarRuntimeEventKind =
  | 'session_ready'
  | 'greeting_finished'
  | 'operator_speech_started'
  | 'operator_turn_committed'
  | 'model_processing_started'
  | 'tool_started'
  | 'tool_finished'
  | 'audio_started'
  | 'audio_progress'
  | 'audio_drained'
  | 'affect_reported'
  | 'barge_in'
  | 'affect_finished'
  | 'failed'
  | 'reset'

export interface AgentOSAvatarRuntimeEvent {
  protocol: typeof AGENTOS_AVATAR_RUNTIME_PROTOCOL
  session_id: string
  turn_id: string | null
  sequence: number
  occurred_at: string
  kind: AgentOSAvatarRuntimeEventKind
  expected_audio_duration?: number
  audible_elapsed?: number
  affect?: AgentOSAvatarAffect
  affect_intensity?: AgentOSAvatarAffectIntensity
}

export type AgentOSAvatarEventDisposition = 'applied' | 'ignored_stale'

export interface NormalizedAgentOSAvatarAffect {
  affect: AgentOSAvatarAffect
  intensity: AgentOSAvatarAffectIntensity | null
}

/** Invalid or inconsistent tool arguments fail safe to the neutral state. */
export function normalizeAgentOSAvatarAffect(input: unknown): NormalizedAgentOSAvatarAffect {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return { affect: 'neutral', intensity: null }
  }
  const value = input as Record<string, unknown>
  if (value.affect === 'pleased' || value.affect === 'concerned') {
    if (value.affect_intensity === 'low' || value.affect_intensity === 'high') {
      return { affect: value.affect, intensity: value.affect_intensity }
    }
  }
  return { affect: 'neutral', intensity: null }
}

/**
 * Ordnet einen eingehenden Function-Call der richtigen Behandlung zu.
 *
 * ANLASS (Alex, 2026-08-02, Post 4439): Der Fuehrer begruesste und sprach
 * danach UNGEFRAGT eine zweite Antwort ("das Wetter wird morgen …"). Beleg
 * aus Session tschepp-err-1785688390677: 0,7 s nach audio_drained kam ein
 * `weather_forecast`-Call — ausgeloest nicht vom Besucher, sondern vom
 * Affect-Followup, das nach jedem Bot-Turn den Gesichtsausdruck erfragt.
 *
 * Das Followup verlangt per Vertrag `tool_choice: "required"` mit GENAU
 * EINEM Werkzeug (`report_affect`, text-only). Der Provider hat sich im
 * realen Lauf darueber hinweggesetzt und ein Session-Werkzeug gewaehlt.
 * Da wir Provider-Verhalten nicht erzwingen koennen, entscheidet der Client
 * fail-closed:
 *
 *   'affect'                  report_affect — der erwartete Ausdrucks-Call
 *   'discard_foreign_affect'  anderes Werkzeug, aber Antwort auf UNSER
 *                             Followup (Response-Korrelation!) — quittieren
 *                             und verwerfen. NIE ausfuehren, NIE eine
 *                             Folge-Antwort ausloesen.
 *   'execute'                 normaler Werkzeug-Aufruf einer echten
 *                             Besucherfrage — unveraendert ausfuehren.
 *
 * Die Korrelation laeuft ueber die Response-ID, nicht ueber die Zeit: Ein
 * Besucher kann unmittelbar nach der Begruessung eine echte Frage stellen,
 * deren Werkzeug-Call waehrend des noch offenen Followups eintrifft — der
 * traegt eine ANDERE Response-ID und muss ausgefuehrt werden.
 */
export function classifyAvatarFunctionCall(
  name: string,
  responseId: string | null,
  pendingFollowupResponseId: string | null,
): 'affect' | 'discard_foreign_affect' | 'execute' {
  if (name === AGENTOS_AVATAR_AFFECT_TOOL) return 'affect'
  if (
    pendingFollowupResponseId != null
    && responseId != null
    && responseId === pendingFollowupResponseId
  ) return 'discard_foreign_affect'
  return 'execute'
}

/** Accept only the server-issued one-tool, text-only deterministic follow-up. */
export function isAgentOSAvatarAffectFollowup(input: unknown): input is Record<string, unknown> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return false
  const event = input as Record<string, unknown>
  const response = event.response as Record<string, unknown> | undefined
  const modalities = response?.output_modalities
  const tools = response?.tools
  if (event.type !== 'response.create' || response?.tool_choice !== 'required'
    || !Array.isArray(modalities) || modalities.length !== 1 || modalities[0] !== 'text'
    || !Array.isArray(tools) || tools.length !== 1) return false
  const tool = tools[0] as Record<string, unknown> | undefined
  return tool?.type === 'function' && tool.name === AGENTOS_AVATAR_AFFECT_TOOL
}

export class AgentOSAvatarRuntimeViolation extends Error {
  readonly code:
    | 'unsupported_protocol'
    | 'foreign_session'
    | 'turn_required'
    | 'turn_forbidden'
    | 'foreign_turn'
    | 'invalid_audio_duration'
    | 'invalid_audio_progress'
    | 'invalid_speaking_role'
    | 'invalid_affect'

  constructor(
    code:
      | 'unsupported_protocol'
      | 'foreign_session'
      | 'turn_required'
      | 'turn_forbidden'
      | 'foreign_turn'
      | 'invalid_audio_duration'
      | 'invalid_audio_progress'
      | 'invalid_speaking_role'
      | 'invalid_affect',
    message: string,
  ) {
    super(message)
    this.name = 'AgentOSAvatarRuntimeViolation'
    this.code = code
  }
}

/** Deterministic role bucket. Boundary values are shared with iOS fixtures. */
export function speakingRoleForAudibleSeconds(
  duration: number,
): Extract<AgentOSAvatarRole, `talk_${string}`> {
  if (duration <= 4) return 'talk_short'
  if (duration <= 8) return 'talk_medium'
  return 'talk_long'
}

/** Resolve the presentation asset without inspecting transcript text. */
export function roleForAgentOSAvatarProjection(
  projection: AgentOSAvatarProjection,
): AgentOSAvatarRole {
  switch (projection.phase) {
    case 'greeting': return 'greeting'
    case 'listening': return 'listening'
    case 'thinking': return 'thinking'
    case 'speaking': return projection.speaking_role ?? 'talk_short'
    case 'error': return 'idle'
    case 'idle':
      if (projection.affect === 'pleased' && projection.affect_intensity === 'low') return 'happy'
      if (projection.affect === 'pleased' && projection.affect_intensity === 'high') return 'joyful'
      if (projection.affect === 'concerned') return 'sad'
      return 'idle'
  }
}

export function visualStateForAgentOSAvatarProjection(
  projection: AgentOSAvatarProjection,
): AgentOSAvatarVisualState {
  if (projection.phase === 'idle' && projection.affect === 'pleased') return 'pleased'
  if (projection.phase === 'idle' && projection.affect === 'concerned') return 'concerned'
  return projection.phase
}

/**
 * Pure state machine shared semantically with CloudV2 iOS. Provider adapters
 * emit events; renderers consume only the resulting projection.
 */
export class AgentOSAvatarStateMachine {
  private current: AgentOSAvatarProjection
  private expectedAudioDuration: number | null = null

  constructor(sessionId: string, occurredAt = new Date().toISOString()) {
    if (!sessionId.trim()) throw new Error('AgentOS avatar session_id is required')
    this.current = {
      protocol: AGENTOS_AVATAR_RUNTIME_PROTOCOL,
      projection_revision: 0,
      session_id: sessionId,
      turn_id: null,
      sequence: 0,
      occurred_at: occurredAt,
      phase: 'idle',
      speaking_role: null,
      affect: 'neutral',
      affect_intensity: null,
    }
  }

  get projection(): AgentOSAvatarProjection {
    return { ...this.current }
  }

  apply(event: AgentOSAvatarRuntimeEvent): AgentOSAvatarEventDisposition {
    if (event.protocol !== AGENTOS_AVATAR_RUNTIME_PROTOCOL) {
      throw new AgentOSAvatarRuntimeViolation('unsupported_protocol', event.protocol)
    }
    if (event.session_id !== this.current.session_id) {
      throw new AgentOSAvatarRuntimeViolation('foreign_session', event.session_id)
    }
    if (event.sequence <= this.current.sequence) return 'ignored_stale'

    let phase = this.current.phase
    let turnId = this.current.turn_id
    let speakingRole = this.current.speaking_role
    let affect = this.current.affect
    let affectIntensity = this.current.affect_intensity

    switch (event.kind) {
      case 'session_ready':
        this.requireSessionEvent(event)
        phase = 'greeting'
        turnId = null
        speakingRole = null
        affect = 'neutral'
        affectIntensity = null
        this.expectedAudioDuration = null
        break
      case 'greeting_finished':
        this.requireSessionEvent(event)
        phase = 'idle'
        turnId = null
        speakingRole = null
        affect = 'neutral'
        affectIntensity = null
        break
      case 'operator_speech_started':
      case 'barge_in':
        turnId = this.requiredTurn(event)
        phase = 'listening'
        speakingRole = null
        affect = 'neutral'
        affectIntensity = null
        this.expectedAudioDuration = null
        break
      case 'operator_turn_committed':
      case 'model_processing_started':
      case 'tool_started':
        turnId = this.matchingTurn(event, turnId)
        phase = 'thinking'
        speakingRole = null
        affect = 'neutral'
        affectIntensity = null
        break
      case 'tool_finished':
        turnId = this.matchingTurn(event, turnId)
        phase = 'thinking'
        speakingRole = null
        break
      case 'audio_started': {
        turnId = this.matchingTurn(event, turnId)
        if (event.expected_audio_duration != null && event.expected_audio_duration <= 0) {
          throw new AgentOSAvatarRuntimeViolation('invalid_audio_duration', String(event.expected_audio_duration))
        }
        this.expectedAudioDuration = event.expected_audio_duration ?? null
        phase = 'speaking'
        speakingRole = speakingRoleForAudibleSeconds(this.expectedAudioDuration ?? 0)
        affect = 'neutral'
        affectIntensity = null
        break
      }
      case 'audio_progress':
        turnId = this.matchingTurn(event, turnId)
        if (phase !== 'speaking' || event.audible_elapsed == null || event.audible_elapsed < 0) {
          throw new AgentOSAvatarRuntimeViolation('invalid_audio_progress', String(event.audible_elapsed))
        }
        if (this.expectedAudioDuration == null) {
          speakingRole = speakingRoleForAudibleSeconds(event.audible_elapsed)
        }
        break
      case 'audio_drained': {
        turnId = this.matchingTurn(event, turnId)
        const resolvedAffect = event.affect ?? 'neutral'
        const resolvedIntensity = event.affect_intensity ?? null
        this.validateAffect(resolvedAffect, resolvedIntensity)
        phase = 'idle'
        speakingRole = null
        affect = resolvedAffect
        affectIntensity = resolvedIntensity
        this.expectedAudioDuration = null
        break
      }
      case 'affect_reported': {
        turnId = this.matchingTurn(event, turnId)
        if (phase !== 'idle') {
          throw new AgentOSAvatarRuntimeViolation('invalid_affect', `phase=${phase}`)
        }
        const resolvedAffect = event.affect ?? 'neutral'
        const resolvedIntensity = event.affect_intensity ?? null
        this.validateAffect(resolvedAffect, resolvedIntensity)
        speakingRole = null
        affect = resolvedAffect
        affectIntensity = resolvedIntensity
        break
      }
      case 'affect_finished':
        this.matchingTurn(event, turnId)
        phase = 'idle'
        turnId = null
        speakingRole = null
        affect = 'neutral'
        affectIntensity = null
        break
      case 'failed':
        phase = 'error'
        speakingRole = null
        affect = 'neutral'
        affectIntensity = null
        this.expectedAudioDuration = null
        break
      case 'reset':
        phase = 'idle'
        turnId = null
        speakingRole = null
        affect = 'neutral'
        affectIntensity = null
        this.expectedAudioDuration = null
        break
    }

    this.validateProjection(phase, turnId, speakingRole, affect, affectIntensity)
    this.current = {
      protocol: AGENTOS_AVATAR_RUNTIME_PROTOCOL,
      projection_revision: this.current.projection_revision + 1,
      session_id: this.current.session_id,
      turn_id: turnId,
      sequence: event.sequence,
      occurred_at: event.occurred_at,
      phase,
      speaking_role: speakingRole,
      affect,
      affect_intensity: affectIntensity,
    }
    return 'applied'
  }

  private requiredTurn(event: AgentOSAvatarRuntimeEvent): string {
    const turn = event.turn_id?.trim()
    if (!turn) throw new AgentOSAvatarRuntimeViolation('turn_required', event.kind)
    return turn
  }

  private matchingTurn(event: AgentOSAvatarRuntimeEvent, current: string | null): string {
    const turn = this.requiredTurn(event)
    if (current && current !== turn) {
      throw new AgentOSAvatarRuntimeViolation('foreign_turn', `${current} != ${turn}`)
    }
    return turn
  }

  private requireSessionEvent(event: AgentOSAvatarRuntimeEvent): void {
    if (event.turn_id != null) {
      throw new AgentOSAvatarRuntimeViolation('turn_forbidden', event.kind)
    }
  }

  private validateAffect(
    affect: AgentOSAvatarAffect,
    intensity: AgentOSAvatarAffectIntensity | null,
  ): void {
    if ((affect === 'neutral') !== (intensity == null)) {
      throw new AgentOSAvatarRuntimeViolation('invalid_affect', `${affect}/${intensity}`)
    }
  }

  private validateProjection(
    phase: AgentOSAvatarPhase,
    turnId: string | null,
    speakingRole: AgentOSAvatarProjection['speaking_role'],
    affect: AgentOSAvatarAffect,
    affectIntensity: AgentOSAvatarAffectIntensity | null,
  ): void {
    if (phase === 'speaking') {
      if (!speakingRole) throw new AgentOSAvatarRuntimeViolation('invalid_speaking_role', phase)
    } else if (speakingRole != null) {
      throw new AgentOSAvatarRuntimeViolation('invalid_speaking_role', phase)
    }
    if ((phase === 'listening' || phase === 'thinking' || phase === 'speaking') && !turnId) {
      throw new AgentOSAvatarRuntimeViolation('turn_required', phase)
    }
    this.validateAffect(affect, affectIntensity)
  }
}
